import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { updateJavaPendingStatus, updatePhpPendingStatus } from '../api/client';

// Plain React-controlled modal, same pattern as ChangePasswordModal.jsx (see
// that component's own docblock for why - avoids Bootstrap's JS Modal
// fighting with React's re-renders for a modal that owns real form state).
//
// One modal, two sources (2026-08-04) - "Java Pending" (source="java") and
// "PHP Pending" (source="php") rows have different shapes and hit different
// downstream update-status endpoints (see api/client.js's
// updateJavaPendingStatus()/updatePhpPendingStatus() and this repo's
// P2MDashboard::updateJavaPendingStatus()/updatePhpPendingStatus()
// docblocks for what each actually calls and which parts are still flagged
// assumptions, not confirmed contracts). Kept as one component rather than
// two near-identical ones since the actual form (view details -> pick
// paid/rejected -> conditionally require error_message -> submit) is
// identical either way; only the identifier field(s) and the submit call
// differ.
//
// Status values changed from SETTLED/REJECTED to lowercase paid/rejected
// (2026-08-14, explicit user instruction) - this is the literal value both
// P2MDashboard::updatePhpPendingStatus() and updateJavaPendingStatus() now
// forward as-is to instapay-direct's and instapay-router's real external
// update-status APIs (see those methods' docblocks), not just a display
// label, so this rename had to happen on both ends together.
// submitJava (optional) replaces updateJavaPendingStatus for a Java-shaped
// row that must go elsewhere - the stuck-deposit rows (updateAwaitingDepositStatus).
// paidOnly: status locked to "paid" - Java Pending QP2M deposits (accepted
// at Leg 3, waiting for Bancnet's payment instruction).
// rejectOnly: status locked to "rejected" with the code prefilled as DS24 -
// for deposits whose Leg 3 never reached the router, where "paid" is never valid.
export const REJECT_ONLY_WARNING =
  'Confirm in Bancnet first that this payment failed (DS24) or was never made. If the customer later pays this same QR while it is still valid, the router accepts it and Java receives a paid callback after this rejection.';

export default function PendingStatusModal({ show, source, record, onClose, onSuccess, label, submitJava, rejectOnly, paidOnly }) {
  const { email } = useAuth();

  const [status, setStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (show && paidOnly && !rejectOnly) {
      setStatus('paid');
    }
    if (show && rejectOnly) {
      setStatus('rejected');
      setErrorMessage((m) => m || 'DS24');
    }
  }, [show, rejectOnly, paidOnly]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Java Pending's Redis record shape was never confirmed against a live
  // cluster (see P2MDashboard::pendingJava()'s docblock), but the field
  // NAME itself is confirmed (2026-08-05): the user clarified the Java and
  // PHP systems are separate, with their own identifier naming -
  // merchant_order_id (Java) corresponds to client_reference_no (PHP), and
  // instruction_id (Java) corresponds to reference_no (PHP). So
  // merchant_order_id is the right field to look for here, full stop - no
  // fallback to a PHP-side field name like reference_no, which would have
  // been meaningless on a Java record and was a mistake in an earlier pass.
  // merchantOrderId/id are kept only as camelCase/generic-key fallbacks in
  // case the raw Redis record's casing turns out to differ.
  const javaIdentifier = useMemo(() => {
    if (!record || source !== 'java') return null;
    return record.merchant_order_id || record.merchantOrderId || record.id || null;
  }, [record, source]);

  // instruction_id - added 2026-08-06, the Java-side counterpart to PHP
  // Pending's reference_no (see the field-naming note above javaIdentifier).
  // Optional and NOT required for canSubmit - unlike merchant_order_id,
  // there's no confirmation from instapay-router that this is mandatory, so
  // a missing value here shouldn't block submitting an update. Same
  // camelCase-fallback treatment as javaIdentifier in case the raw record's
  // casing differs from what's assumed.
  const javaInstructionId = useMemo(() => {
    if (!record || source !== 'java') return null;
    return record.instruction_id || record.instructionId || null;
  }, [record, source]);

  // client_reference_no (not "transaction_id", which doesn't exist as a
  // column on ibft_sender_transactions at all - corrected 2026-08-05, see
  // api/client.js's updatePhpPendingStatus() docblock).
  const phpReady = source === 'php' && record && record.reference_no && record.client_reference_no;

  if (!show || !record) {
    return null;
  }

  function reset() {
    setStatus('');
    setErrorMessage('');
    setSubmitting(false);
    setSubmitError(null);
    setSuccess(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);

    if (!status) {
      setSubmitError('Choose a status.');
      return;
    }
    if (status === 'rejected' && errorMessage.trim() === '') {
      setSubmitError('error_message is required when status is rejected.');
      return;
    }

    setSubmitting(true);
    try {
      if (source === 'php') {
        await updatePhpPendingStatus({
          referenceNo: record.reference_no,
          clientReferenceNo: record.client_reference_no,
          status,
          errorMessage: status === 'rejected' ? errorMessage.trim() : null,
          updatedBy: email,
          // Already present on every PHP Pending row (pendingPhp()'s own
          // SELECT) - added 2026-08-06, instapay-direct's real API requires
          // it. Not user-entered, just forwarded from the record shown.
          isMerchant: record.is_merchant,
        });
      } else {
        await (submitJava || updateJavaPendingStatus)({
          merchantOrderId: javaIdentifier,
          instructionId: javaInstructionId,
          status,
          errorMessage: status === 'rejected' ? errorMessage.trim() : null,
          updatedBy: email,
        });
      }
      setSuccess(true);
      onSuccess?.(record, status);
    } catch (err) {
      setSubmitError(err.message || 'Could not update status.');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = source === 'php' ? phpReady : Boolean(javaIdentifier);

  return (
    <>
      <div className="modal-backdrop show" onClick={handleClose} />
      <div className="modal show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0">
            <div className="modal-header">
              <h1 className="modal-title h5">
                Transaction details <span className="text-muted small">({label || (source === 'php' ? 'PHP Pending' : 'Java Pending')})</span>
              </h1>
              <button type="button" className="btn-close" aria-label="Close" onClick={handleClose} />
            </div>

            <div className="modal-body">
              <div className="table-responsive mb-3">
                <table className="table table-sm mb-0">
                  <tbody>
                    {Object.entries(record).map(([key, value]) => (
                      <tr key={key}>
                        <th className="text-muted small text-nowrap" style={{ width: '12rem' }}>{key}</th>
                        <td className="small text-break">
                          {value === null || value === undefined
                            ? '—'
                            : typeof value === 'object'
                              ? JSON.stringify(value)
                              : String(value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!canSubmit && (
                <div className="alert alert-warning py-2 small">
                  {source === 'php'
                    ? 'This row is missing reference_no or client_reference_no - cannot submit an update.'
                    : 'Could not find a recognized identifier field (merchant_order_id) on this record - cannot submit an update.'}
                </div>
              )}

              {rejectOnly && !success && (
                <div className="alert alert-warning py-2 small">{REJECT_ONLY_WARNING}</div>
              )}

              {success ? (
                <div className="alert alert-success py-2 small mb-0">Status update submitted.</div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label small">New status</label>
                    <select
                      className="form-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      disabled={!canSubmit}
                      required
                    >
                      {!rejectOnly && !paidOnly && <option value="">Choose…</option>}
                      {!rejectOnly && <option value="paid">paid</option>}
                      {(rejectOnly || !paidOnly) && <option value="rejected">rejected</option>}
                    </select>
                  </div>

                  <div className="mb-1">
                    <label className="form-label small">Error message</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={errorMessage}
                      onChange={(e) => setErrorMessage(e.target.value)}
                      disabled={!canSubmit || status !== 'rejected'}
                      required={status === 'rejected'}
                      placeholder={status === 'rejected' ? 'Reason for rejection…' : 'Only used when status is rejected'}
                    />
                  </div>

                  {submitError && <p className="text-danger small mb-0 mt-2">{submitError}</p>}
                </form>
              )}
            </div>

            <div className="modal-footer">
              {success ? (
                <button type="button" className="btn btn-primary" onClick={handleClose}>
                  Done
                </button>
              ) : (
                <>
                  <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting || !canSubmit}>
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
